import { FormHandles } from '@unform/core';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { format } from 'date-fns';

import { Painel } from '../../../../components/Painel';
import { Icon } from '../../../../components/Icon';
import { GroupedPermissionsEditor } from '../../../../components/GroupedPermissionsEditor';
import {
  IPermission,
  inferRoleLabel,
} from '../../../../utils/permissionPresets';
import { colors } from '../../../../styles/tokens';
import { useModal } from '../../../../hooks/modal';
import { useToast } from '../../../../hooks/toast';
import { api } from '../../../../services/api';
import {
  AdminUsersService,
  IAdminUser,
  IAdminUserMembership,
} from '../../../../services/AdminUsersService';
import { AssociateGymUser } from '../../../../modais/associateGymUser';

import noAvatar from '../../../../assets/no-avatar.svg';

import {
  ActionsColumn,
  ActiveToggleButton,
  AddMembershipCard,
  Avatar,
  BackButton,
  BadgeRow,
  Container,
  CountBadge,
  DetailEmptyState,
  DetailEyebrow,
  DetailGymName,
  DetailHeader,
  DetailMeta,
  DetailPanel,
  Layout,
  MembershipCard,
  MembershipCardMeta,
  MembershipCardName,
  MembershipCardSubtitle,
  MembershipForm,
  PlatformBadge,
  ProfileCard,
  ProfileInfo,
  ProfileMeta,
  ProfileName,
  RemoveButton,
  RoleBadge,
  SaveButton,
  Sidebar,
  SidebarTitle,
  StatusBadge,
} from './styles';

const AdminUserDetail: React.FC = () => {
  const { user_id } = useParams<{ user_id: string }>();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { addModal } = useModal();
  const formRef = useRef<FormHandles>(null);

  const adminUsersService = new AdminUsersService();

  const [user, setUser] = useState<IAdminUser>();
  const [catalog, setCatalog] = useState<IPermission[]>([]);
  const [platformPermission, setPlatformPermission] = useState<IPermission>();
  const [selectedMembershipId, setSelectedMembershipId] = useState<
    string | null
  >(null);
  const [loading, setLoading] = useState(true);
  const [togglingActive, setTogglingActive] = useState(false);
  const [togglingPlatform, setTogglingPlatform] = useState(false);
  const [savingPermissions, setSavingPermissions] = useState(false);
  const [removingMembership, setRemovingMembership] = useState(false);

  const getUser = useCallback(() => {
    if (!user_id) {
      return;
    }

    setLoading(true);
    adminUsersService
      .getUser(user_id)
      .then(response => setUser(response.data))
      .catch(() => {
        addToast({
          type: 'error',
          title: 'Não foi possível carregar o usuário.',
        });
        navigate('/admin/users');
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user_id]);

  useEffect(() => {
    getUser();
  }, [getUser]);

  useEffect(() => {
    api
      .get<IPermission[]>('users/permissions/catalog')
      .then(response => {
        setCatalog(
          response.data.filter(permission => permission.scope === 'gym'),
        );
        setPlatformPermission(
          response.data.find(
            permission => permission.code === 'manage_platform_gyms',
          ),
        );
      })
      .catch(() => undefined);
  }, []);

  // Se o vínculo selecionado sumiu (removido) ou nada foi selecionado
  // ainda, cai no primeiro da lista -- sem vínculo nenhum, fica null.
  useEffect(() => {
    if (!user) {
      return;
    }

    setSelectedMembershipId(current => {
      if (current && user.memberships.some(m => m.id === current)) {
        return current;
      }

      return user.memberships[0]?.id ?? null;
    });
  }, [user]);

  const selectedMembership = useMemo<IAdminUserMembership | undefined>(
    () => user?.memberships.find(m => m.id === selectedMembershipId),
    [user, selectedMembershipId],
  );

  const handleAssociate = useCallback(() => {
    if (!user) {
      return;
    }

    addModal(
      <AssociateGymUser
        fixedEmail={user.email}
        userName={user.fullname}
        onSuccess={getUser}
      />,
    );
  }, [addModal, getUser, user]);

  const handleToggleActive = async () => {
    if (!user) {
      return;
    }

    const nextActive = !user.is_active;

    if (
      !nextActive &&
      !window.confirm(
        `Inativar ${user.fullname}? A conta fica bloqueada em todo o sistema até ser reativada.`,
      )
    ) {
      return;
    }

    try {
      setTogglingActive(true);
      await adminUsersService.setActiveStatus(user.id, nextActive);
      setUser({ ...user, is_active: nextActive });
      addToast({
        type: 'success',
        title: nextActive ? 'Usuário ativado.' : 'Usuário inativado.',
      });
    } catch {
      addToast({ type: 'error', title: 'Erro ao atualizar status.' });
    } finally {
      setTogglingActive(false);
    }
  };

  const handleTogglePlatform = async () => {
    if (!user || !platformPermission) {
      return;
    }

    const isPlatformAdmin = user.platformPermissions.includes(
      platformPermission.code,
    );
    const nextCodes = isPlatformAdmin ? [] : [platformPermission.code];

    if (
      isPlatformAdmin &&
      !window.confirm(
        `Revogar admin de plataforma de ${user.fullname}? A pessoa perde acesso ao painel administrativo (gestão de todas as academias).`,
      )
    ) {
      return;
    }

    try {
      setTogglingPlatform(true);
      await adminUsersService.updatePlatformPermissions(user.id, nextCodes);
      setUser({ ...user, platformPermissions: nextCodes });
      addToast({
        type: 'success',
        title: isPlatformAdmin
          ? 'Admin de plataforma revogado.'
          : 'Admin de plataforma concedido.',
      });
    } catch {
      addToast({ type: 'error', title: 'Erro ao atualizar permissão.' });
    } finally {
      setTogglingPlatform(false);
    }
  };

  const handleSavePermissions = async (data: Record<string, boolean>) => {
    if (!selectedMembership) {
      return;
    }

    const selectedCodes = catalog
      .filter(permission => data[permission.code])
      .map(permission => permission.code);

    try {
      setSavingPermissions(true);
      await adminUsersService.updatePermissions(
        selectedMembership.id,
        selectedCodes,
      );
      addToast({ type: 'success', title: 'Permissões atualizadas.' });
      getUser();
    } catch {
      addToast({ type: 'error', title: 'Erro ao salvar permissões.' });
    } finally {
      setSavingPermissions(false);
    }
  };

  const handleRemoveMembership = async () => {
    if (!selectedMembership) {
      return;
    }

    if (
      !window.confirm(
        `Remover o vínculo com ${selectedMembership.gym.name}? O usuário perde acesso de gestor a essa academia.`,
      )
    ) {
      return;
    }

    try {
      setRemovingMembership(true);
      await adminUsersService.removeMembership(selectedMembership.id);
      addToast({ type: 'success', title: 'Vínculo removido.' });
      setSelectedMembershipId(null);
      getUser();
    } catch {
      addToast({ type: 'error', title: 'Erro ao remover vínculo.' });
    } finally {
      setRemovingMembership(false);
    }
  };

  const selectedInitialData = useMemo(() => {
    if (!selectedMembership) {
      return {};
    }

    return catalog.reduce<Record<string, boolean>>((acc, permission) => {
      acc[permission.code] = selectedMembership.permissions.includes(
        permission.code,
      );
      return acc;
    }, {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalog, selectedMembership?.id, selectedMembership?.permissions]);

  return (
    <Painel loading={loading}>
      <Container>
        <BackButton type="button" onClick={() => navigate('/admin/users')}>
          <Icon name="arrow_back" size={16} color={colors.onSurfaceVariant} />
          Voltar
        </BackButton>

        {!!user && (
          <>
            <ProfileCard>
              <ProfileInfo>
                <Avatar src={user.avatar_url || noAvatar} alt={user.fullname} />
                <div>
                  <ProfileName>{user.fullname}</ProfileName>
                  <ProfileMeta>
                    {user.email} · @{user.username}
                  </ProfileMeta>
                  {user.phone && <ProfileMeta>{user.phone}</ProfileMeta>}
                  <ProfileMeta>
                    Cadastrado em{' '}
                    {format(new Date(user.created_at), 'dd/MM/yyyy')}
                  </ProfileMeta>

                  <BadgeRow>
                    <StatusBadge active={user.is_active}>
                      {user.is_active ? 'Ativo' : 'Inativo'}
                    </StatusBadge>
                    {!!user.platformPermissions.length && (
                      <PlatformBadge>Admin de plataforma</PlatformBadge>
                    )}
                    <CountBadge>
                      {user.memberships.length} academia
                      {user.memberships.length === 1 ? '' : 's'}
                    </CountBadge>
                  </BadgeRow>
                </div>
              </ProfileInfo>

              <ActionsColumn>
                <ActiveToggleButton
                  type="button"
                  active={user.is_active}
                  onClick={handleToggleActive}
                  disabled={togglingActive}
                >
                  {user.is_active ? 'Inativar usuário' : 'Ativar usuário'}
                </ActiveToggleButton>

                {!!platformPermission && (
                  <ActiveToggleButton
                    type="button"
                    active={user.platformPermissions.includes(
                      platformPermission.code,
                    )}
                    onClick={handleTogglePlatform}
                    disabled={togglingPlatform}
                  >
                    {user.platformPermissions.includes(platformPermission.code)
                      ? 'Revogar admin'
                      : 'Conceder admin'}
                  </ActiveToggleButton>
                )}
              </ActionsColumn>
            </ProfileCard>

            <Layout>
              <Sidebar>
                <SidebarTitle>Academias vinculadas</SidebarTitle>

                {user.memberships.map(membership => {
                  const role = inferRoleLabel(membership.permissions);
                  const activeCount = catalog.filter(permission =>
                    membership.permissions.includes(permission.code),
                  ).length;

                  return (
                    <MembershipCard
                      key={membership.id}
                      type="button"
                      selected={membership.id === selectedMembershipId}
                      onClick={() => setSelectedMembershipId(membership.id)}
                    >
                      <MembershipCardName>
                        {membership.gym.name}
                      </MembershipCardName>
                      {(membership.gym.address_city ||
                        membership.gym.address_uf) && (
                        <MembershipCardSubtitle>
                          {[
                            membership.gym.address_city,
                            membership.gym.address_uf,
                          ]
                            .filter(Boolean)
                            .join(' · ')}
                        </MembershipCardSubtitle>
                      )}
                      <MembershipCardMeta>
                        <RoleBadge role={role}>{role}</RoleBadge>
                        <span>
                          {activeCount} de {catalog.length} permissões
                        </span>
                      </MembershipCardMeta>
                    </MembershipCard>
                  );
                })}

                <AddMembershipCard type="button" onClick={handleAssociate}>
                  <Icon name="add" size={16} color={colors.primary} />
                  Vincular academia
                </AddMembershipCard>
              </Sidebar>

              <DetailPanel>
                {selectedMembership ? (
                  <>
                    <DetailHeader>
                      <div>
                        <DetailEyebrow>Permissões em</DetailEyebrow>
                        <DetailGymName>
                          {selectedMembership.gym.name}
                        </DetailGymName>
                        <DetailMeta>
                          Vinculado desde{' '}
                          {format(
                            new Date(selectedMembership.created_at),
                            'dd/MM/yyyy',
                          )}{' '}
                          ·{' '}
                          {
                            catalog.filter(permission =>
                              selectedMembership.permissions.includes(
                                permission.code,
                              ),
                            ).length
                          }{' '}
                          de {catalog.length} permissões ativas
                        </DetailMeta>
                      </div>

                      <RemoveButton
                        type="button"
                        onClick={handleRemoveMembership}
                        disabled={removingMembership}
                      >
                        Remover vínculo
                      </RemoveButton>
                    </DetailHeader>

                    <MembershipForm
                      key={`${selectedMembership.id}-${catalog.length}`}
                      ref={formRef}
                      initialData={selectedInitialData}
                      onSubmit={handleSavePermissions}
                    >
                      <GroupedPermissionsEditor
                        catalog={catalog}
                        formRef={formRef}
                        currentPermissions={selectedMembership.permissions}
                      />

                      <SaveButton
                        name="submit"
                        value="Salvar permissões"
                        loading={savingPermissions}
                        disabled={savingPermissions}
                      />
                    </MembershipForm>
                  </>
                ) : (
                  <DetailEmptyState>
                    Selecione uma academia à esquerda, ou vincule uma nova.
                  </DetailEmptyState>
                )}
              </DetailPanel>
            </Layout>
          </>
        )}
      </Container>
    </Painel>
  );
};

export { AdminUserDetail };
